import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useContents } from '../../hooks/useContents';
import TimelineItem from './TimelineItem';
import SearchBar from '../UI/SearchBar';
import CategoryFilter from '../UI/CategoryFilter';
import TagCloud from '../Tag/TagCloud';
import { 
  getTopicAreas, 
  getTopicArea, 
  getTopicAreaCategories, 
  getTopicAreaTags 
} from '../../api/topicAreaApi';
import { TopicArea, Category, Tag, Content } from '../../types';
import './Timeline.css';
import './TimelineAxis.css';
import { buildCategoryHierarchy } from '../../utils/categoryUtils';
import MobileTimelineView from './MobileTimelineView';
import { useAuth } from '../../hooks/useAuth';
import { toast } from 'react-toastify';
import {
  clearContentReturnState,
  ContentReturnNavigationState,
  getContentReturnState,
  markCurrentHistoryForContentReturn,
  saveContentReturnState
} from '../../utils/contentReturnState';

interface SelectedAreaInfo {
  topicArea: TopicArea | null;
  categories: Category[];
  tags: Tag[];
}

interface TimelineContentPosition {
  content: Content;
  xPosition: number; // 가로 축 마커 기준 X 좌표
  isAbove: boolean;   // 가로 축 위(true) 또는 아래(false)
}

// Function to get year color based on year
const getYearColor = (year: number, minYear: number, maxYear: number) => {
  const gradientColors = [
    '#ff9800', // 오렌지
    '#ff5722', // 딥 오렌지
    '#e91e63', // 핑크
    '#9c27b0', // 퍼플
    '#673ab7', // 딥 퍼플
    '#3f51b5', // 인디고
    '#2196f3', // 블루
    '#00bcd4', // 시안
    '#4caf50'  // 그린
  ];
  
  if (minYear === maxYear) {
    return gradientColors[0];
  }
  
  const position = (maxYear - year) / (maxYear - minYear);
  const colorIndex = Math.min(Math.floor(position * (gradientColors.length - 1)), gradientColors.length - 1);
  
  return gradientColors[colorIndex];
};

// Layout Constants
const BASE_MONTH_WIDTH = 280; // 한 달 눈금 너비 (px)
const CARD_WIDTH = 240;       // 카드 너비 (css와 일치)
const CARD_HEIGHT_ESTIMATE = 300;
const CANVAS_TOP_PADDING = 24;
const CANVAS_BOTTOM_PADDING = 20;
const PREFERRED_CARD_AXIS_GAP = 56;
const MIN_CARD_AXIS_GAP = 12;
const EVENT_NAV_EDGE_MARGIN = CARD_WIDTH / 2;

const Timeline: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { topicAreaId } = useParams<{ topicAreaId?: string }>();
  const { user, isAdmin, isEditor } = useAuth();
  const isAdminUser = isAdmin();
  const isEditorUser = isEditor();
  const { contents: initialContents, fetchContents } = useContents();
  const [contents, setContents] = useState<Content[]>([]);
  const [searchQ, setSearchQ] = useState('');
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [searchParams] = useSearchParams();
  const [topicAreas, setTopicAreas] = useState<TopicArea[]>([]);
  const [selectedTopicArea, setSelectedTopicArea] = useState<number | null>(null);
  const [selectedAreaInfo, setSelectedAreaInfo] = useState<SelectedAreaInfo>({
    topicArea: null,
    categories: [],
    tags: []
  });
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  // Dynamic Height Detection to occupy whole remaining viewport height
  const [canvasHeight, setCanvasHeight] = useState(620);
  const [timelineAxisData, setTimelineAxisData] = useState<{ type: 'year' | 'month'; label: string; position: number; color?: string }[]>([]);
  const [timelineContentPositions, setTimelineContentPositions] = useState<TimelineContentPosition[]>([]);
  const [timelineTotalWidth, setTimelineTotalWidth] = useState(0);
  const [cardHeights, setCardHeights] = useState<Record<number, number>>({});
  const [showTopicSummary, setShowTopicSummary] = useState(false);
  const [showTagCloud, setShowTagCloud] = useState(false);
  const [isTagCloudPinned, setIsTagCloudPinned] = useState(false);
  const [axisOffsetY, setAxisOffsetY] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [eventNavState, setEventNavState] = useState({
    hasPrevious: false,
    hasNext: false
  });
  const returnStateRef = useRef(
    (location.state as ContentReturnNavigationState | null)?.restoreContentScroll
      ? getContentReturnState(location.pathname)
      : null
  );

  useEffect(() => {
    const handleResize = () => {
      // 윈도우 전체 높이에서 fixed 헤더(60px) + fixed 조작바(60px) = 120px만 차감
      const remainingHeight = Math.max(550, window.innerHeight - 120);
      setCanvasHeight(remainingHeight);
      
      // 모바일 감지 (768px 이하)
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const aboveCardHeights = timelineContentPositions
    .filter(({ isAbove }) => isAbove)
    .map(({ content }) => cardHeights[content.id] || CARD_HEIGHT_ESTIMATE);
  const belowCardHeights = timelineContentPositions
    .filter(({ isAbove }) => !isAbove)
    .map(({ content }) => cardHeights[content.id] || CARD_HEIGHT_ESTIMATE);
  const maxAboveCardHeight = aboveCardHeights.length > 0
    ? Math.max(...aboveCardHeights)
    : CARD_HEIGHT_ESTIMATE;
  const maxBelowCardHeight = belowCardHeights.length > 0
    ? Math.max(...belowCardHeights)
    : CARD_HEIGHT_ESTIMATE;
  const availableAxisGap = (canvasHeight - maxAboveCardHeight - maxBelowCardHeight - CANVAS_TOP_PADDING - CANVAS_BOTTOM_PADDING) / 2;
  const CARD_AXIS_GAP = Math.max(
    MIN_CARD_AXIS_GAP,
    Math.min(PREFERRED_CARD_AXIS_GAP, availableAxisGap)
  );
  const BASE_AXIS_Y = CANVAS_TOP_PADDING + maxAboveCardHeight + CARD_AXIS_GAP;
  const BASE_TOP_CARD_Y = BASE_AXIS_Y - CARD_AXIS_GAP - maxAboveCardHeight;
  const BASE_BOTTOM_CARD_Y = BASE_AXIS_Y + CARD_AXIS_GAP;
  const minAxisOffsetY = -Math.max(0, BASE_TOP_CARD_Y - 8);
  const clampedAxisOffsetY = Math.max(minAxisOffsetY, axisOffsetY);
  const AXIS_Y = BASE_AXIS_Y + clampedAxisOffsetY;
  const BOTTOM_CARD_Y = BASE_BOTTOM_CARD_Y + clampedAxisOffsetY;
  const timelineCanvasHeight = Math.max(
    canvasHeight + Math.max(0, clampedAxisOffsetY),
    BOTTOM_CARD_Y + maxBelowCardHeight + CANVAS_BOTTOM_PADDING
  );

  // Interaction States (Premium Features)
  const [dragOffsets, setDragOffsets] = useState<Record<number, { dx: number; dy: number }>>({});
  const [activeCardLayer, setActiveCardLayer] = useState<number | null>(null);
  const [hoveredCardId, setHoveredCardId] = useState<number | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const cardObserversRef = useRef<Map<number, ResizeObserver>>(new Map());
  const [isDraggingCanvas, setIsDraggingCanvas] = useState(false);
  const hasRestoredScroll = useRef(false);

  useEffect(() => {
    if (returnStateRef.current) return;

    hasRestoredScroll.current = true;
    window.scrollTo({ top: 0, behavior: 'auto' });
    containerRef.current?.scrollTo({ left: 0, behavior: 'auto' });
  }, [location.key, location.pathname]);

  // Memoized sorted contents for layout calculation
  const sortedContents = useMemo(() => {
    return [...contents].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  }, [contents]);

  const orderedTimelineContentPositions = useMemo(() => {
    return [...timelineContentPositions].sort((a, b) => a.xPosition - b.xPosition);
  }, [timelineContentPositions]);

  const updateEventNavigationState = useCallback(() => {
    const container = containerRef.current;
    if (!container || orderedTimelineContentPositions.length === 0) {
      setEventNavState(prev => (
        prev.hasPrevious || prev.hasNext
          ? { hasPrevious: false, hasNext: false }
          : prev
      ));
      return;
    }

    const visibleLeft = container.scrollLeft + EVENT_NAV_EDGE_MARGIN;
    const visibleRight = container.scrollLeft + container.clientWidth - EVENT_NAV_EDGE_MARGIN;
    const hasPrevious = orderedTimelineContentPositions.some(({ xPosition }) => xPosition < visibleLeft);
    const hasNext = orderedTimelineContentPositions.some(({ xPosition }) => xPosition > visibleRight);

    setEventNavState(prev => (
      prev.hasPrevious === hasPrevious && prev.hasNext === hasNext
        ? prev
        : { hasPrevious, hasNext }
    ));
  }, [orderedTimelineContentPositions]);

  const scrollToAdjacentEvent = useCallback((direction: 'previous' | 'next') => {
    const container = containerRef.current;
    if (!container || orderedTimelineContentPositions.length === 0) return;

    const visibleLeft = container.scrollLeft + EVENT_NAV_EDGE_MARGIN;
    const visibleRight = container.scrollLeft + container.clientWidth - EVENT_NAV_EDGE_MARGIN;
    const target = direction === 'previous'
      ? [...orderedTimelineContentPositions].reverse().find(({ xPosition }) => xPosition < visibleLeft)
      : orderedTimelineContentPositions.find(({ xPosition }) => xPosition > visibleRight);

    if (!target) return;

    const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);
    const nextScrollLeft = Math.min(
      maxScrollLeft,
      Math.max(0, target.xPosition - container.clientWidth / 2)
    );

    setActiveCardLayer(target.content.id);
    container.scrollTo({
      left: nextScrollLeft,
      behavior: 'smooth'
    });
  }, [orderedTimelineContentPositions]);

  const handleContentClick = useCallback((contentId: number) => {
    saveContentReturnState({
      returnTo: location.pathname,
      scrollX: containerRef.current?.scrollLeft || 0,
      scrollY: window.scrollY
    });
    markCurrentHistoryForContentReturn();
    navigate(`/content/${contentId}`, { state: { returnTo: location.pathname } });
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (hasRestoredScroll.current || sortedContents.length === 0) return;
    if (!isMobile && timelineTotalWidth === 0) return;

    const returnState = returnStateRef.current;
    if (!returnState) {
      hasRestoredScroll.current = true;
      return;
    }

    const timer = window.setTimeout(() => {
      if (isMobile) {
        window.scrollTo({ top: returnState.scrollY || 0, behavior: 'auto' });
      } else if (containerRef.current) {
        containerRef.current.scrollTo({ left: returnState.scrollX || 0, behavior: 'auto' });
      }
      clearContentReturnState(location.pathname);
      hasRestoredScroll.current = true;
      returnStateRef.current = null;
    }, 0);

    return () => window.clearTimeout(timer);
  }, [isMobile, location.pathname, sortedContents.length, timelineTotalWidth]);

  // 1. Calculate horizontal positions for years, months, and content cards
  const calculateTimelineLayout = useCallback(() => {
    if (sortedContents.length === 0) {
      setTimelineAxisData([]);
      setTimelineContentPositions([]);
      setTimelineTotalWidth(0);
      return;
    }

    const earliestContentDate = new Date(sortedContents[0].timestamp);
    const latestContentDate = new Date(sortedContents[sortedContents.length - 1].timestamp);
    const currentActualDate = new Date();
    const topicArea = selectedAreaInfo.topicArea;
    const useCompactAxis = topicArea?.timeline_display_compact !== 'N';

    // Group contents by YYYY-MM
    const contentsByMonth = new Map<string, Content[]>();
    sortedContents.forEach(content => {
      const date = new Date(content.timestamp);
      const yearMonthKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
      if (!contentsByMonth.has(yearMonthKey)) {
        contentsByMonth.set(yearMonthKey, []);
      }
      contentsByMonth.get(yearMonthKey)!.push(content);
    });

    const yearsMonthsLayout: { year: number; month: number; contents: Content[]; width: number; cumulativeWidth: number }[] = [];
    let currentCumulativeWidth = 100; // Start offset padding

    const appendMonthLayout = (year: number, month: number) => {
      const yearMonthKey = `${year}-${month.toString().padStart(2, '0')}`;
      const monthlyContents = contentsByMonth.get(yearMonthKey) || [];

      const monthWidth = BASE_MONTH_WIDTH + (Math.max(0, monthlyContents.length - 1) * 120);

      yearsMonthsLayout.push({
        year,
        month,
        contents: monthlyContents,
        width: monthWidth,
        cumulativeWidth: currentCumulativeWidth,
      });
      currentCumulativeWidth += monthWidth;
    };

    if (useCompactAxis) {
      Array.from(contentsByMonth.keys())
        .sort()
        .forEach(yearMonthKey => {
          const [year, month] = yearMonthKey.split('-').map(Number);
          appendMonthLayout(year, month);
        });
    } else {
      const topicStartDate = topicArea?.start_year
        ? new Date(topicArea.start_year, (topicArea.start_month || 1) - 1, 1)
        : earliestContentDate;
      const topicEndDate = topicArea?.is_current === 'Y'
        ? currentActualDate
        : topicArea?.end_year
          ? new Date(topicArea.end_year, topicArea.end_month || 12, 0, 23, 59, 59, 999)
          : latestContentDate;

      const minDate = new Date(
        Math.min(
          new Date(earliestContentDate.getFullYear(), earliestContentDate.getMonth(), 1).getTime(),
          topicStartDate.getTime()
        )
      );
      let maxDate = new Date(
        Math.max(
          latestContentDate.getTime(),
          topicEndDate.getTime()
        )
      );
      maxDate = new Date(maxDate.getFullYear(), maxDate.getMonth() + 1, 0, 23, 59, 59, 999);

      let currentDateIterator = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
      while (currentDateIterator.getTime() <= maxDate.getTime()) {
        appendMonthLayout(currentDateIterator.getFullYear(), currentDateIterator.getMonth() + 1);
        currentDateIterator.setMonth(currentDateIterator.getMonth() + 1);
      }
    }
    
    // Total Canvas width including ending padding
    setTimelineTotalWidth(currentCumulativeWidth + 150);

    // Generate axis markers data
    const newTimelineAxisData: { type: 'year' | 'month'; label: string; position: number; color?: string }[] = [];
    let lastRenderedYear = -1;
    const allYears = yearsMonthsLayout.map(item => item.year);
    const minYear = Math.min(...allYears);
    const maxYear = Math.max(...allYears);

    yearsMonthsLayout.forEach(ym => {
      // Month Marker in the middle of month's width
      newTimelineAxisData.push({
        type: 'month',
        label: `${ym.month}월`,
        position: ym.cumulativeWidth + ym.width / 2,
      });

      // Year Marker at the start of first month of the year
      if (ym.year !== lastRenderedYear) {
        newTimelineAxisData.push({
          type: 'year',
          label: `${ym.year}년`,
          position: ym.cumulativeWidth + 10,
          color: getYearColor(ym.year, minYear, maxYear)
        });
        lastRenderedYear = ym.year;
      }
    });

    // Calculate zoneWidth for each Year Marker to define sticky sliding bounds
    const yearMarkers = newTimelineAxisData.filter(item => item.type === 'year');
    newTimelineAxisData.forEach(marker => {
      if (marker.type === 'year') {
        const currentIndex = yearMarkers.findIndex(ym => ym.position === marker.position);
        const nextYearMarker = yearMarkers[currentIndex + 1];
        
        if (nextYearMarker) {
          (marker as any).zoneWidth = nextYearMarker.position - marker.position - 40;
        } else {
          (marker as any).zoneWidth = Math.max(1200, currentCumulativeWidth - marker.position);
        }
      }
    });

    setTimelineAxisData(newTimelineAxisData);

    // Calculate content card positions (Alternate Above / Below Axis)
    const newTimelineContentPositions: TimelineContentPosition[] = [];

    yearsMonthsLayout.forEach(ym => {
      if (ym.contents.length === 0) return;
      
      const numContents = ym.contents.length;
      const startX = ym.cumulativeWidth;
      const availableWidth = ym.width;

      ym.contents.forEach((content, index) => {
        // Distribute cards evenly inside the month's width range
        const step = availableWidth / (numContents + 1);
        const cardX = startX + step * (index + 1);
        
        // Alternate top (above) and bottom (below) the horizontal axis
        const isAbove = index % 2 === 0;

        newTimelineContentPositions.push({
          content,
          xPosition: cardX,
          isAbove
        });
      });
    });

    setTimelineContentPositions(newTimelineContentPositions);
  }, [sortedContents, selectedAreaInfo.topicArea]);

  // Update contents only after a topic area is selected.
  useEffect(() => {
    if (selectedTopicArea) {
      setContents(initialContents);
    } else {
      setContents([]);
    }
  }, [initialContents, selectedTopicArea]);

  // Load topic areas on mount
  useEffect(() => {
    const loadTopicAreas = async () => {
      try {
        const isOnlyMine = !!(user && !isAdminUser && !isEditorUser);
        const areas = await getTopicAreas(isOnlyMine);
        setTopicAreas(areas);
      } catch (error) {
        console.error('주제영역 목록을 불러오는 중 오류가 발생했습니다:', error);
      }
    };
    
    loadTopicAreas();
  }, [user?.id, isAdminUser, isEditorUser]);

  // Category list loaded on mount
  useEffect(() => {
    buildCategoryHierarchy().then((cats) => setAllCategories(cats as any));
  }, []);

  const handleSearch = async (query: string) => {
    setSearchQ(query);
    await fetchContents(query, categoryId, selectedTopicArea, selectedTag);
  };

  const handleCategorySelect = (selectedCategoryId: number | null) => {
    setCategoryId(selectedCategoryId);
    fetchContents(searchQ, selectedCategoryId, selectedTopicArea, selectedTag);
  };

  const handleTopicAreaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const topicId = e.target.value;
    hasRestoredScroll.current = true;
    returnStateRef.current = null;
    window.scrollTo({ top: 0, behavior: 'auto' });
    containerRef.current?.scrollTo({ left: 0, behavior: 'auto' });
    if (topicId) {
      navigate(`/timeline/${topicId}`);
    } else {
      navigate('/timeline');
    }
  };

  const handleTagSelect = (tag: string | null) => {
    setSelectedTag(tag);
    fetchContents(searchQ, categoryId, selectedTopicArea, tag);
    if (!isTagCloudPinned) {
      setShowTagCloud(false);
    }
  };

  // Load selected topic area when URL changes
  const selectedTopicAreaIdParam = searchParams.get('selectedTopicAreaId');
  useEffect(() => {
    const loadSelectedTopicArea = async () => {
      console.log('DEBUG: loadSelectedTopicArea called', { topicAreaId, selectedTopicAreaIdParam, selectedTag });
      const activeId = topicAreaId || selectedTopicAreaIdParam;
      
      if (activeId) {
        try {
          const area = await getTopicArea(Number(activeId));
          const [categories, tags] = await Promise.all([
            getTopicAreaCategories(area.id),
            getTopicAreaTags(area.id)
          ]);
          
          setSelectedTopicArea(area.id);
          setSelectedAreaInfo({
            topicArea: area,
            categories,
            tags
          });
          
          await fetchContents(searchQ, categoryId, area.id, selectedTag);
        } catch (error) {
          console.error('주제영역 정보를 불러오는 중 오류가 발생했습니다:', error);
        }
      } else {
        setSelectedTopicArea(null);
        setContents([]);
        setSelectedAreaInfo({
          topicArea: null,
          categories: [],
          tags: []
        });
      }
    };
    
    loadSelectedTopicArea();
  }, [topicAreaId, selectedTopicAreaIdParam, selectedTag]);

  // Recalculate layout when sorted contents changes
  useEffect(() => {
    calculateTimelineLayout();
  }, [sortedContents, calculateTimelineLayout]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    updateEventNavigationState();
    container.addEventListener('scroll', updateEventNavigationState, { passive: true });
    window.addEventListener('resize', updateEventNavigationState);

    return () => {
      container.removeEventListener('scroll', updateEventNavigationState);
      window.removeEventListener('resize', updateEventNavigationState);
    };
  }, [updateEventNavigationState, timelineCanvasHeight]);

  // Horizontal mouse wheel scrolling (triggers container scroll)
  const handleWheel = (e: React.WheelEvent) => {
    if (containerRef.current) {
      containerRef.current.scrollLeft += e.deltaY;
    }
  };

  // Canvas-wide Drag-to-scroll implementation (updates container scroll)
  const handleContainerMouseDown = (e: React.MouseEvent) => {
    // Avoid triggering when user clicks on a draggable card, dropdowns or buttons
    if (
      (e.target as HTMLElement).closest('.timeline-card-wrapper') || 
      (e.target as HTMLElement).closest('.timeline-control-bar') ||
      (e.target as HTMLElement).closest('button') ||
      (e.target as HTMLElement).closest('select')
    ) {
      return;
    }
    
    setIsDraggingCanvas(true);
    const startX = e.clientX; 
    const scrollLeft = containerRef.current ? containerRef.current.scrollLeft : 0;
    
    const handleWindowMouseMove = (moveEvent: MouseEvent) => {
      // 윈도우 스크롤 시 텍스트 셀렉션 등 기본 드래그 방지
      moveEvent.preventDefault();
      const walk = (moveEvent.clientX - startX) * 1.5;
      if (containerRef.current) {
        containerRef.current.scrollLeft = scrollLeft - walk;
      }
    };
    
    const handleWindowMouseUp = () => {
      setIsDraggingCanvas(false);
      window.removeEventListener('mousemove', handleWindowMouseMove);
      window.removeEventListener('mouseup', handleWindowMouseUp);
    };
    
    // 윈도우 전역에 이벤트를 붙여 브라우저 외곽 이탈 시에도 포커스를 잃어버리는 현상 완벽 방지
    window.addEventListener('mousemove', handleWindowMouseMove, { passive: false });
    window.addEventListener('mouseup', handleWindowMouseUp);
  };

  // Card click layer priority toggling & click details navigation
  const handleCardClick = (contentId: number) => {
    setActiveCardLayer(contentId);
  };

  const getMeasuredCardHeight = (contentId: number) => (
    cardHeights[contentId] || CARD_HEIGHT_ESTIMATE
  );

  const registerTimelineCard = (contentId: number) => (node: HTMLDivElement | null) => {
    const existingObserver = cardObserversRef.current.get(contentId);
    if (existingObserver) {
      existingObserver.disconnect();
      cardObserversRef.current.delete(contentId);
    }

    if (!node) return;

    const updateHeight = () => {
      const nextHeight = Math.ceil(node.getBoundingClientRect().height);
      if (!nextHeight) return;

      setCardHeights(prev => (
        Math.abs((prev[contentId] || 0) - nextHeight) <= 1
          ? prev
          : { ...prev, [contentId]: nextHeight }
      ));
    };

    updateHeight();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateHeight);
      observer.observe(node);
      cardObserversRef.current.set(contentId, observer);
    }
  };

  useEffect(() => {
    return () => {
      cardObserversRef.current.forEach(observer => observer.disconnect());
      cardObserversRef.current.clear();
    };
  }, []);

  const handleAxisHandleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const startY = e.clientY;
    const startOffset = clampedAxisOffsetY;

    const handleWindowMouseMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault();
      const nextOffset = startOffset + (moveEvent.clientY - startY);
      setAxisOffsetY(Math.max(minAxisOffsetY, nextOffset));
    };

    const handleWindowMouseUp = () => {
      window.removeEventListener('mousemove', handleWindowMouseMove);
      window.removeEventListener('mouseup', handleWindowMouseUp);
    };

    window.addEventListener('mousemove', handleWindowMouseMove, { passive: false });
    window.addEventListener('mouseup', handleWindowMouseUp);
  };

  // Card Mouse Draggable Logic (Rubber Band Connection Line)
  const handleCardMouseDown = (contentId: number, isAbove: boolean, startX: number, startY: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    setActiveCardLayer(contentId);
    
    const currentOffset = dragOffsets[contentId] || { dx: 0, dy: 0 };
    const initialDx = currentOffset.dx;
    const initialDy = currentOffset.dy;
    
    let hasMoved = false;
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;
      
      if (Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4) {
        hasMoved = true;
      }
      
      setDragOffsets(prev => ({
        ...prev,
        [contentId]: {
          dx: initialDx + deltaX,
          dy: initialDy + deltaY
        }
      }));
    };
    
    const handleMouseUp = (upEvent: MouseEvent) => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      
      // 드래그 움직임이 없었다면 (클릭 판정) 상세 화면으로 이동
      if (!hasMoved) {
        handleContentClick(contentId);
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  if (!selectedTopicArea) {
    return (
      <div className="topic-select-container">
        <div className="aurora-bg">
          <div className="aurora-orb aurora-orb-1"></div>
          <div className="aurora-orb aurora-orb-2"></div>
          <div className="aurora-orb aurora-orb-3"></div>
        </div>
        <div className="topic-select-header">
          <h2>📅 역사 아카이브 타임라인</h2>
          <p>시간선에 배치된 역사적 사건들을 확인하려면 아래에서 주제영역을 선택해 주세요.</p>
        </div>
        
        {topicAreas.length === 0 ? (
          <div className="topic-select-empty">
            <div className="empty-icon">📂</div>
            <h3>등록된 주제영역이 없습니다.</h3>
            {user && (
              <button 
                onClick={() => navigate('/topic-areas/new')} 
                className="modern-btn modern-btn-primary"
                style={{ marginTop: 16 }}
              >
                ✨ 첫 주제영역 등록하기
              </button>
            )}
          </div>
        ) : (
          <div className="topic-select-grid">
            {topicAreas.map(area => (
              <div 
                key={area.id} 
                className="topic-select-card glass-panel"
                onClick={() => navigate(`/timeline/${area.id}`)}
              >
                <div className="card-icon">📂</div>
                <h3>{area.title}</h3>
                <p>{area.summary}</p>
                <div className="card-footer">
                  <span>타임라인 탐색하기 ➔</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (isMobile) {
    return (
      <MobileTimelineView
        contents={contents}
        searchQ={searchQ}
        onSearch={handleSearch}
        categoryId={categoryId}
        onCategorySelect={handleCategorySelect}
        selectedTopicArea={selectedTopicArea}
        onTopicAreaChange={handleTopicAreaChange}
        topicAreas={topicAreas}
        allCategories={allCategories}
        selectedAreaInfo={selectedAreaInfo}
        showTopicSummary={showTopicSummary}
        onToggleTopicSummary={() => setShowTopicSummary(!showTopicSummary)}
        onContentClick={handleContentClick}
      />
    );
  }

  return (
    <div className="main-container timeline-fullscreen-layout">
      <div className="aurora-bg">
        <div className="aurora-orb aurora-orb-1"></div>
        <div className="aurora-orb aurora-orb-2"></div>
        <div className="aurora-orb aurora-orb-3"></div>
      </div>
      
      {/* 1줄로 fixed되는 슬림 조작바 */}
      <div className="timeline-control-bar glass-panel">
        <div className="control-bar-left">
          <h2 className="control-bar-title">타임라인</h2>
          <SearchBar onSearch={(q) => handleSearch(q)} />
        </div>
        
        <div className="control-bar-right">
          <CategoryFilter onSelect={handleCategorySelect} />
          
          <div className="topic-area-selector topic-select-dropdown">
            <select
              value={selectedTopicArea || ''}
              onChange={handleTopicAreaChange}
            >
              <option value="">주제영역 선택</option>
              {topicAreas.map(area => (
                <option key={area.id} value={area.id}>
                  {area.title}
                </option>
              ))}
            </select>
          </div>

          {selectedAreaInfo.topicArea && (
            <button 
              className={`control-summary-toggle-btn ${showTopicSummary ? 'active' : ''}`}
              onClick={() => setShowTopicSummary(!showTopicSummary)}
            >
              주제 요약 {showTopicSummary ? '▲' : '▼'}
            </button>
          )}

          <button
            className={`control-tag-toggle-btn ${showTagCloud ? 'active' : ''} ${selectedTag ? 'filtered' : ''}`}
            onClick={() => setShowTagCloud(!showTagCloud)}
            title="태그 필터 패널 열기/접기"
          >
            태그 {selectedTag ? `#${selectedTag}` : ''}
          </button>

          {showTagCloud && (
            <button
              className={`control-tag-pin-btn ${isTagCloudPinned ? 'active' : ''}`}
              onClick={() => setIsTagCloudPinned(!isTagCloudPinned)}
              title={isTagCloudPinned ? '태그 패널 핀 해제' : '태그 패널 핀 고정'}
            >
              {isTagCloudPinned ? '핀 해제' : '핀 고정'}
            </button>
          )}

          <button 
            className="control-share-btn" 
            onClick={() => {
              const url = `${window.location.origin}/timeline/${selectedTopicArea}`;
              navigator.clipboard.writeText(url);
              toast.success('타임라인 공유 링크가 클립보드에 복사되었습니다!');
            }}
            title="현재 타임라인 링크 복사"
          >
            공유하기 🔗
          </button>
          <button 
            className="control-fullscreen-reset-btn" 
            onClick={() => window.scrollTo({ left: 0, behavior: 'smooth' })}
            title="타임라인 처음으로 이동"
          >
            처음으로 🔄
          </button>
        </div>

        {/* 주제영역 요약 카드 (슬라이딩 드롭다운 팝오버 오버레이 형태) */}
        {selectedAreaInfo.topicArea && showTopicSummary && (
          <div className="selected-topic-card-popover glass-panel">
            <div className="popover-header">
              <h3>
                <a 
                  href={`/topic-areas/${selectedAreaInfo.topicArea.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    window.location.href = `/topic-areas/${selectedAreaInfo.topicArea?.id}`;
                  }}
                >
                  {selectedAreaInfo.topicArea.title}
                </a>
              </h3>
              <button className="popover-close-btn" onClick={() => setShowTopicSummary(false)}>×</button>
            </div>
            
            <div className="topic-meta-badges">
              {[1,2,3].map(num => {
                let catId: number | null | undefined = null;
                if (num === 1) catId = selectedAreaInfo.topicArea?.main_category1_id;
                if (num === 2) catId = selectedAreaInfo.topicArea?.main_category2_id;
                if (num === 3) catId = selectedAreaInfo.topicArea?.main_category3_id;
                if (!catId) return null;
                const cat = allCategories.find(c => c.id === catId);
                return (
                  <span key={num} className="topic-cat-badge">
                    📂 {cat ? cat.displayName : `카테고리${num}`}
                  </span>
                );
              })}
              
              {selectedAreaInfo.tags.length > 0 && 
                selectedAreaInfo.tags.map(tag => (
                  <span key={tag.id} className="topic-tag-badge">
                    🏷️ {tag.name}
                  </span>
                ))
              }
            </div>
            
            <p>{selectedAreaInfo.topicArea.summary}</p>
            
            <div className="topic-info-grid">
              <div>
                <div className="topic-info-item-label">기간</div>
                <div className="topic-info-item-val">
                  {selectedAreaInfo.topicArea.start_year}년 
                  {selectedAreaInfo.topicArea.start_month ? ` ${selectedAreaInfo.topicArea.start_month}월` : ''} ~ 
                  {selectedAreaInfo.topicArea.is_current === 'Y' ? ' 현재' : 
                    ` ${selectedAreaInfo.topicArea.end_year}년 ${selectedAreaInfo.topicArea.end_month ? `${selectedAreaInfo.topicArea.end_month}월` : ''}`}
                </div>
              </div>
              
              {selectedAreaInfo.categories.length > 0 && (
                <div>
                  <div className="topic-info-item-label">카테고리</div>
                  <div className="topic-info-chips">
                    {selectedAreaInfo.categories.map((category: Category) => (
                      <span key={category.id} className="topic-info-chip">
                        {category.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showTagCloud && (
        <div className={`timeline-tag-panel glass-panel ${isTagCloudPinned ? 'pinned' : 'floating'}`}>
          <div className="timeline-tag-panel-tools">
            <span className="timeline-tag-panel-title">
              태그 필터 {selectedTag ? `#${selectedTag}` : ''}
            </span>
            <div className="timeline-tag-panel-actions">
              <button
                type="button"
                className={`timeline-tag-panel-btn ${isTagCloudPinned ? 'active' : ''}`}
                onClick={() => setIsTagCloudPinned(!isTagCloudPinned)}
              >
                {isTagCloudPinned ? '핀 해제' : '핀 고정'}
              </button>
              <button
                type="button"
                className="timeline-tag-panel-btn"
                onClick={() => setShowTagCloud(false)}
              >
                접기
              </button>
            </div>
          </div>
          <TagCloud selectedTag={selectedTag} onSelectTag={handleTagSelect} />
        </div>
      )}

      {/* 가로 타임라인 영역 (가로 너비를 직접 대입하여 바디 스크롤바 유도) */}
      <div className="timeline-scroll-stage" style={{ height: `${timelineCanvasHeight}px` }}>
        {(eventNavState.hasPrevious || eventNavState.hasNext) && (
          <div
            className="timeline-event-nav-overlay"
            style={{ top: `${AXIS_Y + 46}px` }}
          >
            {eventNavState.hasPrevious && (
              <button
                type="button"
                className="timeline-event-nav-btn previous"
                onClick={() => scrollToAdjacentEvent('previous')}
                title="이전 사건"
                aria-label="이전 사건"
              >
                &larr;
              </button>
            )}
            {eventNavState.hasNext && (
              <button
                type="button"
                className="timeline-event-nav-btn next"
                onClick={() => scrollToAdjacentEvent('next')}
                title="다음 사건"
                aria-label="다음 사건"
              >
                &rarr;
              </button>
            )}
          </div>
        )}

      <div 
        className="horizontal-timeline-container"
        style={{ 
          height: `${timelineCanvasHeight}px`
        }}
        ref={containerRef}
        onWheel={handleWheel}
        onMouseDown={handleContainerMouseDown}
        onDragStart={(e) => e.preventDefault()} /* 브라우저 자체 드래그 시작 억제하여 포커스 해제 원천 차단 */
      >
        <div 
          className="timeline-canvas-wrapper" 
          style={{ 
            width: timelineTotalWidth > 0 ? `${timelineTotalWidth}px` : '100%',
            minWidth: timelineTotalWidth > 0 ? `${timelineTotalWidth}px` : '100%'
          }}
        >
          <button
            type="button"
            className="timeline-axis-drag-handle"
            style={{ top: `${AXIS_Y}px` }}
            onMouseDown={handleAxisHandleMouseDown}
            onDoubleClick={() => setAxisOffsetY(0)}
            title="타임라인 기준선 위아래 이동"
            aria-label="타임라인 기준선 위아래 이동"
          >
            ↕
          </button>

          {/* 가로 선 (timelineTotalWidth px 만큼 직접 가로 너비 매핑) */}
          <div 
            className="timeline-horizontal-line" 
            style={{
              width: timelineTotalWidth > 0 ? `${timelineTotalWidth}px` : '100%',
              top: `${AXIS_Y}px`
            }}
          />

          {/* 눈금 마커 (년, 월) */}
          {timelineAxisData.map((item, index) => (
            <React.Fragment key={index}>
              {item.type === 'year' && (
                <>
                  {/* 1. 축 위의 년도 도트 마커 (정확한 시간선 자리에 고정) */}
                  <div
                    className="timeline-item-marker year-marker-circle-only"
                    style={{
                      left: `${item.position}px`,
                      top: `${AXIS_Y}px`,
                    }}
                  >
                    <div className="year-circle" style={{ borderColor: item.color }}></div>
                  </div>

                  {/* 2. 가로로 찰칵 고정되어 우측 끝에서 따라오는 대형 년도 라벨 */}
                  <div
                    className="timeline-year-sticky-zone"
                    style={{
                      position: 'absolute',
                      left: `${item.position}px`,
                      width: `${(item as any).zoneWidth || 600}px`,
                      top: `${AXIS_Y - 54}px`, /* 수평 가로축선 바로 위에 위치하도록 Y 좌표 조율 */
                      height: '50px',
                      pointerEvents: 'none',
                      zIndex: 15
                    }}
                  >
                    <div 
                      className="timeline-year-label-sticky"
                      style={{ color: item.color }}
                    >
                      {item.label}
                    </div>
                  </div>
                </>
              )}
              {item.type === 'month' && (
                <div
                  className="timeline-item-marker"
                  style={{
                    left: `${item.position}px`,
                    top: `${AXIS_Y}px`,
                  }}
                >
                  <div className="timeline-month-label">
                    {item.label}
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}

          {/* SVG 탄성 연결선 렌더링 (timelineTotalWidth px 만큼 직접 가로 너비 매핑) */}
          <svg 
            className="timeline-svg-layer"
            style={{ width: timelineTotalWidth > 0 ? `${timelineTotalWidth}px` : '100%' }}
          >
            {timelineContentPositions.map(({ content, xPosition, isAbove }) => {
              const offset = dragOffsets[content.id] || { dx: 0, dy: 0 };
              const measuredCardHeight = getMeasuredCardHeight(content.id);
              
              // 1. 축의 날짜 마커 위치 (중앙선 Y = AXIS_Y)
              const startX = xPosition;
              const startY = AXIS_Y;
              
              // 2. 카드의 현재 최종 좌표 (카드 너비 절반 = CARD_WIDTH / 2)
              const cardBaseY = isAbove ? AXIS_Y - CARD_AXIS_GAP - measuredCardHeight : BOTTOM_CARD_Y;
              const finalCardX = xPosition - (CARD_WIDTH / 2) + offset.dx;
              const finalCardY = cardBaseY + offset.dy;
              
              // 카드의 연결선이 닿을 지점 (위쪽 카드면 카드의 아랫면 중앙, 아래쪽 카드면 카드의 윗면 중앙)
              const targetX = finalCardX + (CARD_WIDTH / 2);
              const targetY = isAbove ? finalCardY + measuredCardHeight : finalCardY;

              const isHovered = hoveredCardId === content.id;
              const isDragging = activeCardLayer === content.id;
              
              return (
                <line
                  key={`line-${content.id}`}
                  x1={startX}
                  y1={startY}
                  x2={targetX}
                  y2={targetY}
                  className={`elastic-connector ${isHovered || isDragging ? 'hovered' : ''}`}
                  stroke={isAbove ? 'var(--primary)' : 'var(--accent)'}
                />
              );
            })}
          </svg>

          {/* 타임라인 카드 배치 */}
          {timelineContentPositions.length === 0 ? (
            <div className="timeline-empty-state" style={{ position: 'absolute', left: '50px', right: '50px', top: '150px' }}>
              {selectedTopicArea 
                ? '이 주제영역에 등록된 컨텐츠가 없습니다.' 
                : '주제영역을 선택하거나 검색어를 입력해주세요.'}
            </div>
          ) : (
            timelineContentPositions.map(({ content, xPosition, isAbove }) => {
              const offset = dragOffsets[content.id] || { dx: 0, dy: 0 };
              const measuredCardHeight = getMeasuredCardHeight(content.id);
              const cardBaseY = isAbove ? AXIS_Y - CARD_AXIS_GAP - measuredCardHeight : BOTTOM_CARD_Y;
              
              // X좌표는 카드의 중앙이 xPosition에 정렬되도록 조정
              const finalLeft = xPosition - (CARD_WIDTH / 2) + offset.dx;
              const finalTop = cardBaseY + offset.dy;
              
              const isLayerActive = activeCardLayer === content.id;
              const isDragging = activeCardLayer === content.id;

              return (
                <div
                  ref={registerTimelineCard(content.id)}
                  key={content.id}
                  className={`timeline-card-wrapper ${isLayerActive ? 'active-layer' : ''} ${isDragging ? 'dragging' : ''}`}
                  style={{
                    left: `${finalLeft}px`,
                    top: `${finalTop}px`,
                    transform: hoveredCardId === content.id ? 'scale(1.02)' : 'scale(1)',
                  }}
                  onMouseDown={(e) => {
                    handleCardMouseDown(content.id, isAbove, e.clientX, e.clientY, e);
                  }}
                  onClick={() => handleCardClick(content.id)}
                  onMouseEnter={() => setHoveredCardId(content.id)}
                  onMouseLeave={() => setHoveredCardId(null)}
                >
                  <TimelineItem content={content} searchQuery={searchQ} categories={allCategories} />
                </div>
              );
            })
          )}
        </div>
      </div>
      </div>
    </div>
  );
};
export default Timeline;
